import { Overlay } from '@radix-ui/react-dialog'
import { cn } from '@seen/ui'
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
} from 'react'

export type OverlayProps = ComponentPropsWithoutRef<typeof Overlay>

export interface DialogOverlayProps extends OverlayProps {
  forceMount?: OverlayProps['forceMount']
  children?: OverlayProps['children']
  asChild?: OverlayProps['asChild']
  /**
   * Data attributes:
   *   [data-state]: "open" | "closed"
   */
  className?: OverlayProps['className']
}

/* z-portal puts the dialog overlay on the same stacking layer as popovers/tooltips/dropdowns
   (`packages/ui/src/baseTailwindConfig.ts`). DOM order then handles the rest: a popover that
   opens the dialog sits earlier in the DOM and renders below; popovers/comboboxes opened
   from inside the dialog sit later and render above. Without this, dialogs default to z=auto
   and any popover (z-portal) that's still open paints above the dialog — see
   `ConditionCreateConfirmDialog.tsx` for the original write-up. Sonner toasts (z=999999999)
   remain above. */
export const DialogOverlay = forwardRef<
  ElementRef<typeof Overlay>,
  DialogOverlayProps
>(({ className, ...delegated }, ref) => (
  <Overlay
    ref={ref}
    className={cn(
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-portal bg-overlay/80 data-[state=closed]:animate-out data-[state=open]:animate-in',
      className
    )}
    {...delegated}
  />
))
DialogOverlay.displayName = Overlay.displayName
